// src/pages/analytics/ArFocusTab.tsx
//
// A/R → Collections (feature analytics.arfocus, agent 3.20.0+). Two PBI
// pages in one tab:
//   • AR Focus (recipe ar-focus): pick a "selection" window of the last N
//     days (slider). Per area / salesman / category: billed and collected in
//     the window, how much of the window's own invoices is still open, open
//     A/R older than the window, total open today, and the over-90 share.
//   • Payments by aging bucket (recipe ar-payment-buckets): which bucket each
//     payment cleared from (payment date − invoice date) — pie, weekly
//     stacks (by bucket or by the customer's terms), and a dimension × bucket
//     matrix, with an optional days-range filter on the payment's age.
// Only the filter pane's customer-side slicers apply here (A/R has no item
// lines); the pane says so.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Target, RefreshCw, Download, FileSpreadsheet, AlertTriangle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, PieChart, Pie, Cell } from 'recharts';
import { apiV2 as api } from '../../services/apiV2';
import type { ArFocusRow, ArPaymentBucketsResponse } from '../../types';
import { exportCsv, type CsvColumns } from '../../lib/csvExport';
import { exportXlsx } from '../../lib/xlsxExport';
import { FilterPane } from './filterPane';
import { useFilterPane } from './filterPaneState';

const fmtCur = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
const fmtK = (v: number) => `${v < 0 ? '-' : ''}$${(Math.abs(v) / 1000).toFixed(0)}k`;

const DIMENSIONS = [
  { value: 'area',             label: 'Area' },
  { value: 'salesman',         label: 'Salesman' },
  { value: 'customerCategory', label: 'Customer Category' },
  { value: 'priceLevel',       label: 'Price Level' },
  { value: 'customer',         label: 'Customer' },
];
const WEEKS = [8, 13, 26, 52];
// Current → oldest, then Unapplied (grey).
const BUCKET_COLORS = ['#10b981', '#84cc16', '#f59e0b', '#f97316', '#ef4444', '#64748b'];
const TERMS_COLORS = ['#3b82f6', '#a855f7', '#14b8a6', '#f59e0b', '#ec4899', '#06b6d4', '#84cc16', '#64748b'];

const selectCls = 'px-2 py-1.5 rounded-md bg-slate-800/50 border border-slate-700/50 text-xs text-white focus:outline-none focus:ring-1 focus:ring-blue-500/50';
const btnCls = 'px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40';
const pill = (on: boolean) => `px-2 py-1.5 text-[11px] rounded-md border transition-all ${
  on ? 'bg-blue-500/15 text-blue-200 border-blue-500/40'
     : 'border-slate-700/50 text-slate-500 hover:text-slate-300 bg-slate-900/40'}`;
const tooltipStyle = { backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px' };

function exportFocus(rows: ArFocusRow[], totals: ArFocusRow | undefined, dim: string, days: number, exporter: typeof exportCsv = exportCsv) {
  const cols: CsvColumns<ArFocusRow> = [
    [dim, (r) => r.label],
    [`Sales (${days}d)`, (r) => r.sales],
    [`Payments (${days}d)`, (r) => r.payments],
    ['Collected %', (r) => r.collectedPct],
    ['Open in window', (r) => r.openSelected],
    ['Open past window', (r) => r.openPast],
    ['A/R today', (r) => r.arToday],
    ['Over 90', (r) => r.over90],
    ['Over 90 %', (r) => r.over90Pct],
  ];
  exporter(`ar-focus-${days}d`, cols, totals ? [...rows, totals] : rows);
}

function exportBuckets(data: ArPaymentBucketsResponse, dim: string, exporter: typeof exportCsv = exportCsv) {
  type M = ArPaymentBucketsResponse['matrix'][number];
  const cols: CsvColumns<M> = [
    [dim, (r) => r.label],
    ...data.buckets.map((b, i): [string, (r: M) => number] => [b, (r) => r.values[i]?.amount ?? 0]),
    ['Total', (r) => r.total],
  ];
  exporter('payments-by-bucket', cols, data.matrix);
}

function ArFocusSection({ paneKey, paneParams }: { paneKey: string; paneParams: Record<string, string | undefined> }) {
  const [days, setDays] = useState(14);
  const [dimension, setDimension] = useState('area');
  const q = useQuery({
    queryKey: ['arFocus', days, dimension, paneKey],
    queryFn: () => api.getArFocus({ days, dimension }, paneParams),
    staleTime: 60_000,
    retry: 1,
  });
  const rows = q.data?.rows ?? [];
  const totals = q.data?.totals;
  const dimLabel = DIMENSIONS.find(d => d.value === dimension)?.label ?? dimension;
  const th = 'px-3 py-2.5 text-right text-slate-400 font-medium whitespace-nowrap';

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3 p-4 rounded-xl bg-slate-800/20 border border-slate-700/30">
        <div className="flex flex-col gap-1 min-w-[220px]">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Selection — last {days} day{days === 1 ? '' : 's'}</span>
          <input type="range" min={0} max={90} step={1} value={days} onChange={e => setDays(Number(e.target.value))}
            className="accent-blue-500" aria-label="Selection window in days" />
        </div>
        <div className="flex items-center gap-1.5">
          {[7, 14, 30, 45].map(n => <button key={n} onClick={() => setDays(n)} className={pill(days === n)}>{n}d</button>)}
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">By</span>
          <select value={dimension} onChange={e => setDimension(e.target.value)} className={selectCls}>
            {DIMENSIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
        </div>
        <div className="flex-1" />
        <button onClick={() => exportFocus(rows, totals, dimLabel, days)} disabled={rows.length === 0} className={btnCls}><Download size={11} /> CSV</button>
        <button onClick={() => exportFocus(rows, totals, dimLabel, days, exportXlsx)} disabled={rows.length === 0} className={btnCls}><FileSpreadsheet size={11} /> Excel</button>
        <button onClick={() => q.refetch()} disabled={q.isLoading}
          className="p-2 rounded-md bg-slate-700/50 border border-slate-600/50 text-slate-400 hover:text-white transition-all disabled:opacity-50">
          <RefreshCw size={14} className={q.isFetching ? 'animate-spin' : ''} />
        </button>
      </div>

      {totals && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            ['Sales', fmtCur(totals.sales), `last ${days}d`],
            ['Payments', fmtCur(totals.payments), `${totals.collectedPct.toFixed(1)}% of sales`],
            ['Open in window', fmtCur(totals.openSelected), `invoices from last ${days}d`],
            ['Open past window', fmtCur(totals.openPast), `older than ${days}d`],
            ['A/R today', fmtCur(totals.arToday), `${totals.over90Pct.toFixed(1)}% over 90`],
          ].map(([label, value, sub]) => (
            <div key={label} className="p-3 rounded-xl border border-slate-700/50 bg-slate-800/30">
              <div className="text-[11px] text-slate-400 uppercase tracking-wider">{label}</div>
              <div className="text-xl font-semibold text-white tabular-nums mt-1">{value}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>
            </div>
          ))}
        </div>
      )}

      {q.error ? (
        <div className="p-4 rounded-md bg-red-950/30 border border-red-900/40 text-red-200 text-sm flex items-center gap-2">
          <AlertTriangle size={16} /> Couldn't load AR Focus — {(q.error as Error).message}
        </div>
      ) : q.isLoading ? (
        <div className="flex items-center justify-center h-32"><RefreshCw className="w-6 h-6 text-blue-400 animate-spin" /></div>
      ) : (
        <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-700/50 bg-slate-800/50">
                  <th className="sticky left-0 z-10 bg-slate-800 px-3 py-2.5 text-left text-slate-400 font-medium min-w-[160px]">{dimLabel}</th>
                  <th className={th}>Sales</th>
                  <th className={th}>Payments</th>
                  <th className={th}>Collected %</th>
                  <th className={`${th} border-l border-slate-700/40`}>Open in window</th>
                  <th className={th}>Open past window</th>
                  <th className={th}>A/R today</th>
                  <th className={`${th} border-l border-slate-700/40`}>Over 90</th>
                  <th className={th}>Over 90 %</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={`${r.label}-${i}`} className="group border-b border-slate-800/50 hover:bg-slate-700/20 transition-colors">
                    <td className="sticky left-0 z-10 bg-slate-900 group-hover:bg-slate-800 px-3 py-2 text-white whitespace-nowrap max-w-[240px] truncate" title={r.label}>{r.label}</td>
                    <td className="px-3 py-2 text-right text-slate-200 tabular-nums">{fmtCur(r.sales)}</td>
                    <td className="px-3 py-2 text-right text-emerald-300/90 tabular-nums">{fmtCur(r.payments)}</td>
                    <td className="px-3 py-2 text-right text-slate-400 tabular-nums">{r.sales ? `${r.collectedPct.toFixed(1)}%` : '–'}</td>
                    <td className="px-3 py-2 text-right text-slate-300 tabular-nums border-l border-slate-800/60">{fmtCur(r.openSelected)}</td>
                    <td className="px-3 py-2 text-right text-amber-300/90 tabular-nums">{fmtCur(r.openPast)}</td>
                    <td className="px-3 py-2 text-right text-white tabular-nums font-medium">{fmtCur(r.arToday)}</td>
                    <td className="px-3 py-2 text-right text-red-300/90 tabular-nums border-l border-slate-800/60">{fmtCur(r.over90)}</td>
                    <td className={`px-3 py-2 text-right tabular-nums ${r.over90Pct >= 28 ? 'text-red-400 font-medium' : 'text-slate-400'}`}>{r.arToday ? `${r.over90Pct.toFixed(1)}%` : '–'}</td>
                  </tr>
                ))}
              </tbody>
              {totals && rows.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-slate-600/60 bg-slate-800/60 font-semibold">
                    <td className="sticky left-0 z-10 bg-slate-800 px-3 py-2 text-white">Total</td>
                    <td className="px-3 py-2 text-right text-white tabular-nums">{fmtCur(totals.sales)}</td>
                    <td className="px-3 py-2 text-right text-white tabular-nums">{fmtCur(totals.payments)}</td>
                    <td className="px-3 py-2 text-right text-slate-300 tabular-nums">{totals.collectedPct.toFixed(1)}%</td>
                    <td className="px-3 py-2 text-right text-white tabular-nums border-l border-slate-700/60">{fmtCur(totals.openSelected)}</td>
                    <td className="px-3 py-2 text-right text-white tabular-nums">{fmtCur(totals.openPast)}</td>
                    <td className="px-3 py-2 text-right text-white tabular-nums">{fmtCur(totals.arToday)}</td>
                    <td className="px-3 py-2 text-right text-white tabular-nums border-l border-slate-700/60">{fmtCur(totals.over90)}</td>
                    <td className="px-3 py-2 text-right text-slate-300 tabular-nums">{totals.over90Pct.toFixed(1)}%</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          {rows.length === 0 && <div className="text-center py-8 text-slate-500 text-sm">No open A/R or activity in this window</div>}
          <div className="px-3 py-2 border-t border-slate-700/30 text-xs text-slate-500">
            Open balances are the sum of open invoices (not the customer balance field). Over 90 % highlighted red at 28%+.
          </div>
        </div>
      )}
    </div>
  );
}

function PaymentBucketsSection({ paneKey, paneParams }: { paneKey: string; paneParams: Record<string, string | undefined> }) {
  const [weeks, setWeeks] = useState(13);
  const [dimension, setDimension] = useState('customerCategory');
  const [stackBy, setStackBy] = useState<'bucket' | 'terms'>('bucket');
  const [minDays, setMinDays] = useState('');
  const [maxDays, setMaxDays] = useState('');
  const q = useQuery({
    queryKey: ['arPaymentBuckets', weeks, dimension, minDays, maxDays, paneKey],
    queryFn: () => api.getArPaymentBuckets({ weeks, dimension, minDays: minDays || undefined, maxDays: maxDays || undefined }, paneParams),
    staleTime: 60_000,
    retry: 1,
  });
  const data = q.data;
  const dimLabel = DIMENSIONS.find(d => d.value === dimension)?.label ?? dimension;
  const pie = (data?.totals ?? []).filter(t => t.amount > 0);
  const weekly = stackBy === 'bucket'
    ? (data?.weekly ?? []).map(w => ({ label: w.label, ...Object.fromEntries(w.values.map(v => [v.bucket, v.amount])) }))
    : (data?.weeklyByTerms ?? []).map(w => ({ label: w.label, ...Object.fromEntries(w.values.map(v => [v.terms, v.amount])) }));
  const series = stackBy === 'bucket' ? (data?.buckets ?? []) : (data?.terms ?? []);
  const colors = stackBy === 'bucket' ? BUCKET_COLORS : TERMS_COLORS;
  const th = 'px-3 py-2.5 text-right text-slate-400 font-medium whitespace-nowrap';

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3 p-4 rounded-xl bg-slate-800/20 border border-slate-700/30">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Weeks</span>
          <select value={weeks} onChange={e => setWeeks(Number(e.target.value))} className={selectCls}>
            {WEEKS.map(w => <option key={w} value={w}>{w} weeks</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Matrix rows</span>
          <select value={dimension} onChange={e => setDimension(e.target.value)} className={selectCls}>
            {DIMENSIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Days range (payment age)</span>
          <div className="flex items-center gap-1.5">
            <input type="number" min={0} value={minDays} onChange={e => setMinDays(e.target.value)} placeholder="min"
              className="w-16 px-2 py-1.5 rounded-md bg-slate-800/50 border border-slate-700/50 text-xs text-white" />
            <span className="text-slate-500 text-xs">to</span>
            <input type="number" min={0} value={maxDays} onChange={e => setMaxDays(e.target.value)} placeholder="max"
              className="w-16 px-2 py-1.5 rounded-md bg-slate-800/50 border border-slate-700/50 text-xs text-white" />
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Weekly stack by</span>
          <div className="flex items-center gap-1.5">
            <button onClick={() => setStackBy('bucket')} className={pill(stackBy === 'bucket')}>Aging bucket</button>
            <button onClick={() => setStackBy('terms')} className={pill(stackBy === 'terms')}>Terms</button>
          </div>
        </div>
        <div className="flex-1" />
        <button onClick={() => data && exportBuckets(data, dimLabel)} disabled={!data} className={btnCls}><Download size={11} /> CSV</button>
        <button onClick={() => data && exportBuckets(data, dimLabel, exportXlsx)} disabled={!data} className={btnCls}><FileSpreadsheet size={11} /> Excel</button>
      </div>

      {q.error ? (
        <div className="p-4 rounded-md bg-red-950/30 border border-red-900/40 text-red-200 text-sm flex items-center gap-2">
          <AlertTriangle size={16} /> Couldn't load payments by bucket — {(q.error as Error).message}
        </div>
      ) : q.isLoading || !data ? (
        <div className="flex items-center justify-center h-32"><RefreshCw className="w-6 h-6 text-blue-400 animate-spin" /></div>
      ) : (
        <>
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 p-4">
              <h3 className="text-sm font-semibold text-white">Payments by bucket</h3>
              <p className="text-[11px] text-slate-500 mb-2">
                {fmtCur(data.total)} received {data.dateFrom} → {data.dateTo}
                {data.avgDaysToPay != null && <> · avg {data.avgDaysToPay} days to pay</>}
              </p>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={pie} dataKey="amount" nameKey="bucket" innerRadius="45%" outerRadius="80%" paddingAngle={1}>
                      {pie.map(p => <Cell key={p.bucket} fill={BUCKET_COLORS[data.buckets.indexOf(p.bucket)] ?? '#64748b'} />)}
                    </Pie>
                    <Tooltip contentStyle={tooltipStyle} itemStyle={{ color: '#f1f5f9' }}
                      formatter={(v, n, item) => [`${fmtCur(Number(v))} (${(item?.payload as { pct?: number })?.pct?.toFixed(1)}%)`, String(n)]} />
                    <Legend wrapperStyle={{ fontSize: 11 }} formatter={(v) => <span style={{ color: '#94a3b8' }}>{v}</span>} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 p-4 xl:col-span-2">
              <h3 className="text-sm font-semibold text-white mb-3">Weekly payments — stacked by {stackBy === 'bucket' ? 'aging bucket' : 'terms'}</h3>
              <div className="h-60">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={weekly} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                    <CartesianGrid vertical={false} stroke="#334155" strokeOpacity={0.4} />
                    <XAxis dataKey="label" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#475569' }} tickLine={false} interval="preserveStartEnd" minTickGap={12} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#475569' }} tickLine={false} tickFormatter={(v) => fmtK(Number(v))} />
                    <Tooltip cursor={{ fill: '#475569', fillOpacity: 0.15 }} contentStyle={tooltipStyle} labelStyle={{ color: '#f1f5f9' }}
                      formatter={(v, n) => [fmtCur(Number(v)), String(n)]} />
                    <Legend wrapperStyle={{ fontSize: 11 }} formatter={(v) => <span style={{ color: '#94a3b8' }}>{v}</span>} />
                    {series.map((s, i) => <Bar key={s} dataKey={s} stackId="a" fill={colors[i % colors.length]} maxBarSize={32} />)}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-700/50 bg-slate-800/50">
                    <th className="sticky left-0 z-10 bg-slate-800 px-3 py-2.5 text-left text-slate-400 font-medium min-w-[160px]">{dimLabel}</th>
                    {data.buckets.map(b => <th key={b} className={th}>{b}</th>)}
                    <th className={`${th} border-l border-slate-700/40`}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.matrix.map((r, i) => (
                    <tr key={`${r.label}-${i}`} className="group border-b border-slate-800/50 hover:bg-slate-700/20 transition-colors">
                      <td className="sticky left-0 z-10 bg-slate-900 group-hover:bg-slate-800 px-3 py-2 text-white whitespace-nowrap max-w-[240px] truncate" title={r.label}>{r.label}</td>
                      {r.values.map((v, vi) => (
                        <td key={vi} className="px-3 py-2 text-right tabular-nums text-slate-300">
                          {v.amount ? fmtCur(v.amount) : <span className="text-slate-600">–</span>}
                          {v.amount !== 0 && r.total !== 0 && <span className="block text-[10px] text-slate-500">{(100 * v.amount / r.total).toFixed(0)}%</span>}
                        </td>
                      ))}
                      <td className="px-3 py-2 text-right tabular-nums text-white border-l border-slate-800/60">{fmtCur(r.total)}</td>
                    </tr>
                  ))}
                </tbody>
                {data.matrix.length > 0 && (
                  <tfoot>
                    <tr className="border-t-2 border-slate-600/60 bg-slate-800/60 font-semibold">
                      <td className="sticky left-0 z-10 bg-slate-800 px-3 py-2 text-white">Total</td>
                      {data.totals.map(t => (
                        <td key={t.bucket} className="px-3 py-2 text-right tabular-nums text-white">
                          {fmtCur(t.amount)}<span className="block text-[10px] text-slate-400 font-normal">{t.pct.toFixed(0)}%</span>
                        </td>
                      ))}
                      <td className="px-3 py-2 text-right tabular-nums text-white border-l border-slate-700/60">{fmtCur(data.total)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
            {data.matrix.length === 0 && <div className="text-center py-8 text-slate-500 text-sm">No payments in this range</div>}
            <div className="px-3 py-2 border-t border-slate-700/30 text-xs text-slate-500">
              Bucket = payment date − invoice date. Unapplied = payments not tied to an invoice date.
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export function ArFocusTab() {
  const pane = useFilterPane();
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-white flex items-center gap-2">
          <Target size={20} /> Collections
        </h2>
        <p className="text-sm text-slate-400 mt-0.5">
          AR Focus for a selection window, and which aging bucket your payments are clearing from.
        </p>
      </div>
      <FilterPane note="Customer filters apply here; item filters and Net/Gross don't affect A/R." />
      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider">AR Focus</h3>
        <ArFocusSection paneKey={pane.key} paneParams={pane.params} />
      </section>
      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider">Payments by aging bucket</h3>
        <PaymentBucketsSection paneKey={pane.key} paneParams={pane.params} />
      </section>
    </div>
  );
}
